import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Phone, PhoneOff, PhoneCall, Play, Pause, Clock, Mic, MicOff, Search, X, ClipboardList, ChevronDown, MessageSquare, Star, FileText, Download } from 'lucide-react'
import { voipApi, leadsApi, usersApi, playbookApi, type Lead, type User } from '../../api/endpoints'
import { useAuthStore } from '../../stores/auth'
import Button from '../../components/ui/Button'
import Input, { Select } from '../../components/ui/Input'
import { StatusBadge, StageBadge, SegmentBadge } from '../../components/ui/Badge'
import Pagination from '../../components/ui/Pagination'
import { useVoIP } from '../../providers/VoIPProvider'
import EmptyState from '../../components/ui/EmptyState'
import CallPulseModal from '../../components/shared/CallPulseModal'
import PostCallLeadPrompt from '../../components/voip/PostCallLeadPrompt'
import { fmtDateTime } from '../../utils/date'
import MobileFilterPanel from '../../components/ui/MobileFilterPanel'

export default function VoIPPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [leadParamLoaded, setLeadParamLoaded] = useState(false)
  const [createLeadCallId, setCreateLeadCallId] = useState<number | null>(null)
  const [createLeadCallerNumber, setCreateLeadCallerNumber] = useState('')

  // Handle ?lead={id} URL param to pre-select a lead
  useEffect(() => {
    if (leadParamLoaded) return
    const params = new URLSearchParams(window.location.search)
    const leadParam = params.get('lead')
    if (leadParam) {
      leadsApi.get(leadParam).then(({ data }) => {
        if (data) {
          const lead = data as Lead
          setSelectedLead(lead)
          setPhoneNumber(lead.phone_number || '')
        }
      }).catch(() => {})
      // Clean up URL param
      window.history.replaceState({}, '', '/voip')
    }
    setLeadParamLoaded(true)
  }, [leadParamLoaded])
  const [playingId, setPlayingId] = useState<number | null>(null)
  const [callPulseOpen, setCallPulseOpen] = useState(false)
  const [callPulsePlaybookId, setCallPulsePlaybookId] = useState<number | undefined>(undefined)
  const [showPlaybookSelector, setShowPlaybookSelector] = useState(false)

  const currentUser = useAuthStore((s) => s.user)
  const isAdminOrManager = currentUser?.role === 'admin' || currentUser?.role === 'manager'

  // Call history filters
  const [historyOffset, setHistoryOffset] = useState(0)
  const [historyFilters, setHistoryFilters] = useState({
    lead_id: '',
    provider: '',
    status: '',
    direction: '',
    agent_id: '',
    date_from: '',
    date_to: '',
  })
  const historyLimit = 15

  // Universal VoIP device — auto-detects provider
  const device = useVoIP()

  const isOnCall = device.status === 'on-call' || device.status === 'connecting'

  // Fetch agents list for the agent filter (admin/manager only)
  const { data: agentsData } = useQuery({
    queryKey: ['users-list'],
    queryFn: () => usersApi.list(),
    enabled: isAdminOrManager,
    staleTime: 5 * 60 * 1000,
  })
  const agents: User[] = (agentsData as any)?.users || []

  // Build query params for call history
  const historyParams: Record<string, string> = {
    limit: String(historyLimit),
    offset: String(historyOffset),
  }
  if (selectedLead) historyParams.lead_id = String(selectedLead.id)
  else if (historyFilters.lead_id) historyParams.lead_id = historyFilters.lead_id
  if (historyFilters.provider) historyParams.provider = historyFilters.provider
  if (historyFilters.status) historyParams.status = historyFilters.status
  if (historyFilters.direction) historyParams.direction = historyFilters.direction
  if (historyFilters.agent_id) historyParams.agent_id = historyFilters.agent_id
  if (historyFilters.date_from) historyParams.date_from = historyFilters.date_from
  if (historyFilters.date_to) historyParams.date_to = historyFilters.date_to

  const { data: recentData } = useQuery({
    queryKey: ['voip-calls', historyParams],
    queryFn: () => voipApi.list(historyParams),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !isOnCall,
  })

  const recentCalls = recentData?.calls || []
  const totalCalls = recentData?.total || 0

  // Create a backend record when calling — no query invalidation on success
  const callMutation = useMutation({
    mutationFn: (data: { lead_id?: number; to_number: string }) => voipApi.call(data as { lead_id: number; to_number: string }),
  })

  // Register outbound call meta with global VoIP context
  useEffect(() => {
    const callId = callMutation.data?.data?.id
    if (callId) {
      device.setActiveCallMeta({
        callId,
        leadId: selectedLead?.id ?? null,
        leadName: selectedLead?.name ?? null,
      })
    }
  }, [callMutation.data?.data?.id])

  // Call outcome sync is handled globally by VoIPProvider
  // Invalidate local queries when call ends
  useEffect(() => {
    if (device.callOutcome !== 'none') {
      queryClient.invalidateQueries({ queryKey: ['voip-calls'] })
    }
  }, [device.callOutcome])

  // Auto-open CallPulse when call is answered
  const prevStatus = useRef(device.status)
  useEffect(() => {
    if (device.status === 'on-call' && prevStatus.current !== 'on-call' && selectedLead) {
      setCallPulseOpen(true)
    }
    prevStatus.current = device.status
  }, [device.status, selectedLead])

  function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const handleSelectLead = (lead: Lead) => {
    setSelectedLead(lead)
    setPhoneNumber(lead.phone_number || '')
  }

  const handleClearLead = () => {
    setSelectedLead(null)
    setPhoneNumber('')
  }

  const handleCall = () => {
    if (!phoneNumber) return
    // Ensure number has + prefix for international dialing
    const dialNumber = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`
    // Create backend call record first — the backend resolves the real
    // phone number (unmasked) and returns it as dial_number for the SDK.
    callMutation.mutate({
      ...(selectedLead ? { lead_id: selectedLead.id } : {}),
      to_number: dialNumber,
    } as { lead_id: number; to_number: string }, {
      onSuccess: (resp) => {
        // Use the backend-resolved dial_number (always unmasked) for the SDK
        const realNumber = resp?.data?.dial_number || dialNumber
        device.connect(realNumber)
      },
      onError: () => {
        // If backend fails, try with the UI number (works when masking is off)
        device.connect(dialNumber)
      },
    })
  }

  const handleEndCall = () => {
    device.disconnect()
    // For REST API providers, also end the call via backend
    if (!device.hasWebRTC && callMutation.data?.data?.id) {
      voipApi.endCall(callMutation.data.data.id).catch(() => {})
    }
    // Status update is handled by the useEffect watching device.callOutcome
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Calls</h1>
        <span className={`text-xs flex items-center gap-1 ${
          device.status === 'ready' ? 'text-green-600' :
          device.status === 'no-webrtc' ? 'text-blue-600' :
          device.status === 'error' ? 'text-red-600' :
          'text-gray-400'
        }`}>
          <span className={`w-2 h-2 rounded-full ${
            device.status === 'ready' ? 'bg-green-500 animate-pulse' :
            device.status === 'no-webrtc' ? 'bg-blue-500' :
            device.status === 'error' ? 'bg-red-500' :
            'bg-gray-400'
          }`} />
          {device.status === 'ready' ? `${device.provider} — Browser calling ready` :
           device.status === 'no-webrtc' ? `${device.provider} — REST API mode (server-initiated calls)` :
           device.status === 'error' ? (device.error || 'Device error') :
           device.status === 'on-call' ? 'On call' :
           device.status === 'connecting' ? 'Connecting...' :
           'Initializing...'}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Dialer + Active Call */}
        <div className="lg:col-span-1 space-y-4">
          {/* Dialer */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Phone size={16} /> Click to Call
            </h2>
            <div className="space-y-3">
              {/* Lead search or selected lead display */}
              {selectedLead ? (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{selectedLead.name}</p>
                      <p className="text-xs text-gray-600">
                        {selectedLead.company || ''}{selectedLead.email ? ` | ${selectedLead.email}` : ''}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <StageBadge stage={selectedLead.stage} />
                        <SegmentBadge segment={selectedLead.quality_segment} />
                      </div>
                    </div>
                    <button onClick={handleClearLead} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X size={14} /></button>
                  </div>
                </div>
              ) : (
                <LeadSearchInput onSelect={handleSelectLead} />
              )}

              <Input
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+91..."
              />
              <Button
                onClick={handleCall}
                disabled={!phoneNumber || isOnCall || (device.status !== 'ready' && device.status !== 'no-webrtc')}
                loading={device.status === 'connecting' || callMutation.isPending}
                className="w-full"
              >
                <PhoneCall size={16} /> {device.hasWebRTC ? 'Call via Browser' : 'Call'}
              </Button>
              {!device.hasWebRTC && device.status === 'no-webrtc' && (
                <p className="text-[11px] text-blue-600 text-center">
                  {device.provider} uses server-initiated calls. Configure a user phone number in the header menu to receive the call.
                </p>
              )}
              {device.error && (
                <p className="text-xs text-red-600">{device.error}</p>
              )}
              {callMutation.error && (
                <p className="text-xs text-red-600">{(callMutation.error as Error).message}</p>
              )}

              {/* CallPulse when not on call but lead is selected */}
              {selectedLead && !isOnCall && (
                <div className="relative pt-3 border-t border-gray-200 mt-3">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        setCallPulsePlaybookId(undefined)
                        setCallPulseOpen(true)
                      }}
                    >
                      <MessageSquare size={14} /> Start CallPulse
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowPlaybookSelector(!showPlaybookSelector)}
                      title="Choose a specific playbook"
                    >
                      <ClipboardList size={14} /> <ChevronDown size={12} />
                    </Button>
                  </div>
                  {showPlaybookSelector && (
                    <PlaybookSelector
                      onSelect={(id) => {
                        setCallPulsePlaybookId(id)
                        setShowPlaybookSelector(false)
                        setCallPulseOpen(true)
                      }}
                      onClose={() => setShowPlaybookSelector(false)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Active Call Widget */}
          {isOnCall && (
            <div className="rounded-xl border-2 p-5 bg-green-50 border-green-300">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Phone size={16} className="text-green-600 animate-pulse" />
                  {device.status === 'connecting' ? 'Connecting...' : 'On Call'}
                </h3>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">To</span>
                  <span className="font-medium">
                    {selectedLead ? (
                      <a
                        href={`/leads/${selectedLead.lead_id || selectedLead.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        {selectedLead.name}
                      </a>
                    ) : phoneNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Duration</span>
                  <span className="font-mono font-medium text-lg">{formatDuration(device.duration)}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={device.toggleMute}
                  className={device.isMuted ? 'text-red-600' : ''}
                >
                  {device.isMuted ? <MicOff size={16} /> : <Mic size={16} />}
                  {device.isMuted ? 'Unmute' : 'Mute'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={device.openNotesPanel}
                  className={device.isNotesPanelOpen ? 'ring-2 ring-indigo-300' : ''}
                >
                  <FileText size={16} /> Notes
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleEndCall}
                  className="flex-1"
                >
                  <PhoneOff size={16} /> End Call
                </Button>
              </div>

              {/* CallPulse trigger during active call */}
              {selectedLead && (
                <div className="relative mt-3">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        setCallPulsePlaybookId(undefined)
                        setCallPulseOpen(true)
                      }}
                    >
                      <MessageSquare size={14} /> CallPulse
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowPlaybookSelector(!showPlaybookSelector)}
                    >
                      <ClipboardList size={14} /> <ChevronDown size={12} />
                    </Button>
                  </div>
                  {showPlaybookSelector && (
                    <PlaybookSelector
                      onSelect={(id) => {
                        setCallPulsePlaybookId(id)
                        setShowPlaybookSelector(false)
                        setCallPulseOpen(true)
                      }}
                      onClose={() => setShowPlaybookSelector(false)}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Call Outcome + Rating (shown after call ends) */}
          {!isOnCall && device.callOutcome !== 'none' && (
            <PostCallRating
              callOutcome={device.callOutcome}
              callId={callMutation.data?.data?.id ?? device.activeCallMeta?.callId}
              contactName={selectedLead?.name || phoneNumber}
              duration={device.duration}
              formatDuration={formatDuration}
            />
          )}

          {/* Unknown caller lead creation prompt */}
          {!isOnCall && device.postCallContext && (
            <PostCallLeadPrompt
              callerNumber={device.postCallContext.callerNumber}
              voipCallId={device.postCallContext.voipCallId}
              onCreated={(leadId, _leadName) => {
                // Update selected lead context so CallPulse and other features work
                leadsApi.get(leadId).then(({ data }) => {
                  if (data) setSelectedLead(data as Lead)
                }).catch(() => {})
                device.clearPostCallContext()
              }}
              onDismiss={device.clearPostCallContext}
            />
          )}
        </div>

        {/* Right: Call History */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200">
            {/* Header with filters */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  {selectedLead ? `Call History — ${selectedLead.name}` : 'Call History'}
                  <span className="text-xs font-normal text-gray-400 ml-2">({totalCalls} calls)</span>
                </h2>
                <div className="flex items-center gap-2">
                  {isAdminOrManager && (
                    <button
                      onClick={() => {
                        const params: Record<string, string> = {}
                        if (historyFilters.provider) params.provider = historyFilters.provider
                        if (historyFilters.status) params.status = historyFilters.status
                        if (historyFilters.direction) params.direction = historyFilters.direction
                        if (historyFilters.agent_id) params.agent_id = historyFilters.agent_id
                        if (historyFilters.date_from) params.date_from = historyFilters.date_from
                        if (historyFilters.date_to) params.date_to = historyFilters.date_to
                        if (selectedLead) params.lead_id = String(selectedLead.id)
                        else if (historyFilters.lead_id) params.lead_id = historyFilters.lead_id
                        voipApi.exportCsv(Object.keys(params).length ? params : undefined)
                      }}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                      title="Export call history as CSV"
                    >
                      <Download size={12} /> Export
                    </button>
                  )}
                </div>
              </div>

              {/* Filter row */}
              <MobileFilterPanel activeCount={[historyFilters.agent_id, historyFilters.direction, historyFilters.provider, historyFilters.status, historyFilters.date_from, historyFilters.date_to].filter(Boolean).length} className="pt-2">
                {isAdminOrManager && (
                  <Select
                    options={[
                      { value: '', label: 'All Agents' },
                      ...agents.map(a => ({ value: String(a.id), label: a.full_name || a.username })),
                    ]}
                    value={historyFilters.agent_id}
                    onChange={(e) => { setHistoryFilters(f => ({ ...f, agent_id: e.target.value })); setHistoryOffset(0) }}
                  />
                )}
                <Select
                  options={[
                    { value: '', label: 'All Directions' },
                    { value: 'inbound', label: 'Inbound' },
                    { value: 'outbound', label: 'Outbound' },
                  ]}
                  value={historyFilters.direction}
                  onChange={(e) => { setHistoryFilters(f => ({ ...f, direction: e.target.value })); setHistoryOffset(0) }}
                />
                <Select
                  options={[
                    { value: '', label: 'All Providers' },
                    { value: 'twilio', label: 'Twilio' },
                    { value: 'plivo', label: 'Plivo' },
                    { value: 'signalwire', label: 'SignalWire' },
                    { value: 'freeswitch', label: 'FreeSWITCH' },
                  ]}
                  value={historyFilters.provider}
                  onChange={(e) => { setHistoryFilters(f => ({ ...f, provider: e.target.value })); setHistoryOffset(0) }}
                />
                <Select
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'abandoned', label: 'Abandoned' },
                    { value: 'no_answer', label: 'No Answer' },
                    { value: 'failed', label: 'Failed' },
                    { value: 'initiated', label: 'Initiated' },
                    { value: 'in_progress', label: 'In Progress' },
                    { value: 'busy', label: 'Busy' },
                  ]}
                  value={historyFilters.status}
                  onChange={(e) => { setHistoryFilters(f => ({ ...f, status: e.target.value })); setHistoryOffset(0) }}
                />
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                  <input
                    type="date"
                    value={historyFilters.date_from}
                    onChange={(e) => { setHistoryFilters(f => ({ ...f, date_from: e.target.value })); setHistoryOffset(0) }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={historyFilters.date_to}
                    onChange={(e) => { setHistoryFilters(f => ({ ...f, date_to: e.target.value })); setHistoryOffset(0) }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </MobileFilterPanel>
            </div>

            {/* Call list */}
            {recentCalls.length === 0 ? (
              <div className="p-8">
                <EmptyState
                  icon={Phone}
                  title="No calls found"
                  description={selectedLead ? `No calls recorded for ${selectedLead.name}` : 'No calls match your filters.'}
                />
              </div>
            ) : (
              <>
                {/* Mobile card view */}
                <div className="md:hidden divide-y divide-gray-100">
                  {recentCalls.map((call) => (
                    <div key={call.id} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            call.direction === 'inbound' ? 'bg-blue-50' : 'bg-green-50'
                          }`}>
                            {call.direction === 'inbound' ? (
                              <PhoneCall size={14} className="text-blue-600" />
                            ) : (
                              <Phone size={14} className="text-green-600" />
                            )}
                          </div>
                          <div className="min-w-0">
                            {call.lead_id ? (
                              <button onClick={() => navigate(`/leads/${call.lead_id}`)} className="text-sm font-medium text-indigo-600 hover:text-indigo-800 truncate block max-w-[180px]">
                                {call.lead_name || 'Unknown'}
                              </button>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-medium text-gray-900">Unknown</span>
                                {call.direction === 'inbound' && (
                                  <button
                                    onClick={() => {
                                      setCreateLeadCallId(call.id)
                                      setCreateLeadCallerNumber(call.from_number || '')
                                    }}
                                    className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded font-medium"
                                  >
                                    + Lead
                                  </button>
                                )}
                              </div>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5">{call.direction === 'inbound' ? call.from_number : call.to_number}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!isOnCall && (call.to_number || call.from_number) && (
                            <button
                              onClick={() => {
                                const raw = call.direction === 'outbound' ? call.to_number : call.from_number
                                const number = raw && !raw.startsWith('+') && /^\d{10,}$/.test(raw) ? `+${raw}` : raw
                                setPhoneNumber(number || '')
                                if (call.lead_id) {
                                  leadsApi.get(call.lead_id).then(({ data }) => {
                                    if (data) setSelectedLead(data as Lead)
                                  }).catch(() => {})
                                } else {
                                  setSelectedLead(null)
                                }
                                window.scrollTo({ top: 0, behavior: 'smooth' })
                              }}
                              className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg"
                              aria-label="Call back"
                            >
                              <Phone size={16} />
                            </button>
                          )}
                          {(call.recording_url || call.recording_storage_path) && (
                            <button
                              onClick={() => setPlayingId(playingId === call.id ? null : call.id)}
                              className={`p-2 rounded-lg ${
                                playingId === call.id ? 'bg-indigo-100 text-indigo-700' : 'text-gray-400 hover:text-indigo-600'
                              }`}
                              aria-label={playingId === call.id ? 'Pause recording' : 'Play recording'}
                            >
                              {playingId === call.id ? <Pause size={16} /> : <Play size={16} />}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <StatusBadge status={call.call_status || call.status} />
                        {call.duration_seconds != null && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <Clock size={10} /> {formatDuration(call.duration_seconds)}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">{fmtDateTime(call.created_at)}</span>
                      </div>
                      {(call.call_outcome || call.call_quality_score) && (
                        <div className="flex items-center gap-2 mt-1.5">
                          {call.call_outcome && (
                            <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] rounded font-medium capitalize">{call.call_outcome}</span>
                          )}
                          {call.call_quality_score && (
                            <span className="text-amber-500 text-[10px]">{'★'.repeat(call.call_quality_score)}{'☆'.repeat(5 - call.call_quality_score)}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop row view */}
                <div className="hidden md:block divide-y divide-gray-100">
                  {recentCalls.map((call) => (
                    <div key={call.id} className="px-3 sm:px-6 py-4 flex items-center gap-3 sm:gap-4">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        call.direction === 'inbound' ? 'bg-blue-50' : 'bg-green-50'
                      }`}>
                        {call.direction === 'inbound' ? (
                          <PhoneCall size={16} className="text-blue-600" />
                        ) : (
                          <Phone size={16} className="text-green-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {call.lead_id ? (
                            <button onClick={() => navigate(`/leads/${call.lead_id}`)} className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline">
                              {call.lead_name || 'Unknown'}
                            </button>
                          ) : (
                            <>
                              <span className="text-sm font-medium text-gray-900">Unknown</span>
                              {call.direction === 'inbound' && (
                                <button
                                  onClick={() => {
                                    setCreateLeadCallId(call.id)
                                    setCreateLeadCallerNumber(call.from_number || '')
                                  }}
                                  className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded font-medium transition-colors"
                                >
                                  + Create Lead
                                </button>
                              )}
                            </>
                          )}
                          <StatusBadge status={call.call_status || call.status} />
                          <span className="text-xs text-gray-400 capitalize">{call.direction}</span>
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded font-medium">{call.provider}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                          <span>{fmtDateTime(call.created_at)}</span>
                          {call.duration_seconds != null && (
                            <span className="flex items-center gap-1">
                              <Clock size={10} /> {formatDuration(call.duration_seconds)}
                            </span>
                          )}
                          <span className="text-gray-400">{call.direction === 'inbound' ? call.from_number : call.to_number}</span>
                          {call.call_outcome && (
                            <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] rounded font-medium capitalize">{call.call_outcome}</span>
                          )}
                          {call.call_quality_score && (
                            <span className="text-amber-500 text-[10px]">{'★'.repeat(call.call_quality_score)}{'☆'.repeat(5 - call.call_quality_score)}</span>
                          )}
                        </div>
                        {call.outcome_notes && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs" title={call.outcome_notes}>{call.outcome_notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!isOnCall && (call.to_number || call.from_number) && (
                          <button
                            onClick={() => {
                              const raw = call.direction === 'outbound' ? call.to_number : call.from_number
                              // Ensure + prefix for international numbers
                              const number = raw && !raw.startsWith('+') && /^\d{10,}$/.test(raw) ? `+${raw}` : raw
                              setPhoneNumber(number || '')
                              if (call.lead_id) {
                                leadsApi.get(call.lead_id).then(({ data }) => {
                                  if (data) setSelectedLead(data as Lead)
                                }).catch(() => {})
                              } else {
                                setSelectedLead(null)
                              }
                              // Scroll to top so user sees the prefilled dialer
                              window.scrollTo({ top: 0, behavior: 'smooth' })
                            }}
                            className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                            title={`Call back ${call.direction === 'inbound' ? call.from_number : (call.lead_name || call.to_number)}`}
                          >
                            <Phone size={16} />
                          </button>
                        )}
                        {(call.recording_url || call.recording_storage_path) && (
                          <button
                            onClick={() => setPlayingId(playingId === call.id ? null : call.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              playingId === call.id ? 'bg-indigo-100 text-indigo-700' : 'text-gray-400 hover:text-indigo-600 hover:bg-gray-100'
                            }`}
                            title="Play recording"
                          >
                            {playingId === call.id ? <Pause size={16} /> : <Play size={16} />}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Audio player */}
            {playingId && (
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                <audio
                  controls
                  autoPlay
                  className="w-full h-8"
                  src={`/api/voip/call/${playingId}/recording`}
                  onEnded={() => setPlayingId(null)}
                />
              </div>
            )}

            {/* Pagination */}
            <div className="px-6 pb-4">
              <Pagination total={totalCalls} limit={historyLimit} offset={historyOffset} onChange={setHistoryOffset} />
            </div>
          </div>
        </div>
      </div>

      {/* CallPulse Modal — auto-opens when call is answered, or manually triggered */}
      {callPulseOpen && (
        <CallPulseModal
          leadRef={selectedLead?.id ?? null}
          voipCallId={(callMutation.data?.data?.id ?? device.activeCallMeta?.callId) as number | undefined}
          playbookId={callPulsePlaybookId}
          callerNumber={device.activeCallNumber}
          onClose={() => { setCallPulseOpen(false); setCallPulsePlaybookId(undefined) }}
          onLeadCreated={(leadId) => {
            leadsApi.get(leadId).then(({ data }) => {
              if (data) setSelectedLead(data as Lead)
            }).catch(() => {})
          }}
        />
      )}

      {/* Create Lead from call history — for orphaned inbound calls */}
      {createLeadCallId && (
        <PostCallLeadPromptModal
          voipCallId={createLeadCallId}
          callerNumber={createLeadCallerNumber}
          onCreated={() => {
            setCreateLeadCallId(null)
            queryClient.invalidateQueries({ queryKey: ['voip-calls'] })
          }}
          onDismiss={() => setCreateLeadCallId(null)}
        />
      )}
    </div>
  )
}

function PostCallRating({
  callOutcome,
  callId,
  contactName,
  duration,
  formatDuration,
}: {
  callOutcome: string
  callId: number | undefined
  contactName: string
  duration: number
  formatDuration: (s: number) => string
}) {
  const queryClient = useQueryClient()
  const [quality, setQuality] = useState(0)
  const [outcome, setOutcome] = useState('')
  const [notes, setNotes] = useState('')
  const [saved, setSaved] = useState(false)

  const rateMutation = useMutation({
    mutationFn: (data: { call_outcome?: string; call_quality_score?: number; outcome_notes?: string }) =>
      voipApi.rateCall(callId!, data),
    onSuccess: () => {
      setSaved(true)
      queryClient.invalidateQueries({ queryKey: ['voip-calls'] })
    },
  })

  const handleSave = () => {
    if (!callId) return
    rateMutation.mutate({
      ...(outcome ? { call_outcome: outcome } : {}),
      ...(quality > 0 ? { call_quality_score: quality } : {}),
      ...(notes ? { outcome_notes: notes } : {}),
    })
  }

  const wrapperClass =
    callOutcome === 'connected' ? 'bg-green-50 border-green-200' :
    callOutcome === 'cancelled' ? 'bg-gray-50 border-gray-200' :
    callOutcome === 'not_connected' ? 'bg-amber-50 border-amber-200' :
    'bg-red-50 border-red-200'

  const iconClass =
    callOutcome === 'connected' ? 'text-green-600' :
    callOutcome === 'cancelled' ? 'text-gray-500' :
    callOutcome === 'not_connected' ? 'text-amber-600' :
    'text-red-600'

  const labelClass =
    callOutcome === 'connected' ? 'text-green-700' :
    callOutcome === 'cancelled' ? 'text-gray-700' :
    callOutcome === 'not_connected' ? 'text-amber-700' :
    'text-red-700'

  return (
    <div className={`rounded-xl border p-4 ${wrapperClass}`}>
      {/* Status header */}
      <div className="flex items-center gap-2 mb-1">
        <PhoneOff size={16} className={iconClass} />
        <span className={`text-sm font-semibold ${labelClass}`}>
          {callOutcome === 'connected' ? 'Connected' :
           callOutcome === 'cancelled' ? 'Cancelled' :
           callOutcome === 'not_connected' ? 'Not Connected' :
           'Failed'}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        {callOutcome === 'connected' && `Call with ${contactName} — ${formatDuration(duration)}`}
        {callOutcome === 'cancelled' && `Call to ${contactName} was cancelled by you`}
        {callOutcome === 'not_connected' && `Call rang but ${contactName} did not pick up`}
        {callOutcome === 'failed' && `Call to ${contactName} failed to connect`}
      </p>

      {/* Rating form — only show for connected calls with a backend call ID */}
      {callOutcome === 'connected' && callId && !saved && (
        <div className="space-y-3 pt-2 border-t border-green-200">
          {/* Star rating */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Call Quality</label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setQuality(star)}
                  className="p-0.5 transition-colors"
                  title={`${star} star${star > 1 ? 's' : ''}`}
                >
                  <Star
                    size={20}
                    className={star <= quality ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                  />
                </button>
              ))}
              {quality > 0 && (
                <span className="text-xs text-gray-500 ml-1">
                  {quality === 1 ? 'Poor' : quality === 2 ? 'Fair' : quality === 3 ? 'Good' : quality === 4 ? 'Very Good' : 'Excellent'}
                </span>
              )}
            </div>
          </div>

          {/* Outcome selector */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Outcome</label>
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">Select outcome...</option>
              <option value="resolved">Resolved</option>
              <option value="escalated">Escalated</option>
              <option value="transferred">Transferred</option>
              <option value="voicemail">Voicemail</option>
              <option value="abandoned">Abandoned</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
              placeholder="Call notes..."
            />
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={rateMutation.isPending || (quality === 0 && !outcome && !notes)}
            className="w-full px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {rateMutation.isPending ? 'Saving...' : 'Save Rating'}
          </button>
          {rateMutation.error && (
            <p className="text-xs text-red-600">{(rateMutation.error as Error).message}</p>
          )}
        </div>
      )}

      {/* Saved confirmation */}
      {saved && (
        <p className="text-xs text-green-600 font-medium mt-2">Rating saved</p>
      )}
    </div>
  )
}

function PlaybookSelector({ onSelect, onClose }: { onSelect: (id: number) => void; onClose: () => void }) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { data } = useQuery({
    queryKey: ['playbooks-list'],
    queryFn: () => playbookApi.list(),
  })
  const playbooks = (data?.data || []) as any[]

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const activePlaybooks = playbooks.filter((p: any) => p.is_active)

  return (
    <div ref={wrapperRef} className="absolute z-30 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-2 space-y-1 right-0">
      <p className="text-xs font-semibold text-gray-500 uppercase px-2 py-1">Select Playbook</p>
      {activePlaybooks.length === 0 && (
        <p className="text-xs text-gray-400 px-2 py-3 text-center">No active playbooks found</p>
      )}
      {activePlaybooks.map((p: any) => (
        <button key={p.id} onClick={() => onSelect(p.id)}
          className="w-full text-left px-3 py-2 rounded-lg hover:bg-indigo-50 transition-colors">
          <p className="text-sm font-medium text-gray-900">{p.name}</p>
          {p.description && <p className="text-xs text-gray-500 mt-0.5">{p.description}</p>}
        </button>
      ))}
    </div>
  )
}

function PostCallLeadPromptModal({
  voipCallId,
  callerNumber,
  onCreated,
  onDismiss,
}: {
  voipCallId: number
  callerNumber: string
  onCreated: () => void
  onDismiss: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={(e) => { if (e.target === e.currentTarget) onDismiss() }}>
      <div className="fixed inset-0 bg-black/40" />
      <div className="relative w-full max-w-md">
        <PostCallLeadPrompt
          voipCallId={voipCallId}
          callerNumber={callerNumber}
          onCreated={onCreated}
          onDismiss={onDismiss}
        />
      </div>
    </div>
  )
}

function LeadSearchInput({ onSelect }: { onSelect: (lead: Lead) => void }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const { data } = useQuery({
    queryKey: ['lead-search', query],
    queryFn: () => leadsApi.list({ search: query, limit: '8' }),
    enabled: query.length >= 2,
  })

  const results = data?.leads || []

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">Search Lead</label>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => { if (query.length >= 2) setOpen(true) }}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Name, email, or phone..."
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {results.map((lead) => (
            <button
              key={lead.id}
              onClick={() => { onSelect(lead); setQuery(''); setOpen(false) }}
              className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 border-b border-gray-50 last:border-0"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{lead.name}</p>
                  <p className="text-xs text-gray-500">
                    {[lead.company, lead.email, lead.phone_number].filter(Boolean).join(' | ')}
                  </p>
                </div>
                {lead.phone_number && (
                  <Phone size={13} className="text-green-500 flex-shrink-0" />
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && query.length >= 2 && results.length === 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="text-xs text-gray-500 text-center">No leads found for "{query}"</p>
        </div>
      )}
    </div>
  )
}
